/**
 * Environment configuration for the Caire application
 * This file centralizes all environment-specific settings
 */
import { getLocalizedPath } from "@/utils/routes";

// Base URL for the application
// We default to https://www.caire.se if not defined in environment variables
export const BASE_URL = import.meta.env.VITE_BASE_URL || "https://www.caire.se";

// API endpoint
export const API_URL = import.meta.env.VITE_API_URL || `${BASE_URL}/api`;

// Cal.com booking link ("username/event-type") behind the marketing "Boka demo" flow
export const CAL_BOOKING_LINK =
  import.meta.env.VITE_CAL_BOOKING_LINK || "caire/30min";

// Function to generate a full URL with the base URL
export const getFullUrl = (path: string): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.substring(1) : path;
  return `${BASE_URL}/${cleanPath}`;
};

// Function to generate a canonical URL based on the current language
export const getCanonicalUrl = (path: string, language: string): string => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const routeMappedPath = getLocalizedPath(
    normalizedPath,
    language === "en" ? "en" : "sv",
  );

  if (routeMappedPath !== normalizedPath) {
    return `${BASE_URL}${routeMappedPath}`;
  }

  // For English, add /en prefix if not already present
  if (language === "en" && !normalizedPath.startsWith("/en")) {
    return `${BASE_URL}/en${normalizedPath}`;
  }

  // For Swedish, remove /en prefix if present
  if (language === "sv" && normalizedPath.startsWith("/en")) {
    const svPath = normalizedPath.replace(/^\/en/, "") || "/";
    return `${BASE_URL}${svPath}`;
  }

  // Return as-is for correct language paths
  return `${BASE_URL}${normalizedPath}`;
};

// Function to get language-specific URLs for hreflang tags
export const getLanguageUrls = (
  path: string | undefined,
): { svUrl: string; enUrl: string } => {
  if (!path) {
    return {
      svUrl: `${BASE_URL}`,
      enUrl: `${BASE_URL}/en`,
    };
  }

  // Ensure path starts with /
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  const localizedSvPath = getLocalizedPath(normalizedPath, "sv");
  const localizedEnPath = getLocalizedPath(normalizedPath, "en");

  // Use the central route map for translated slugs. Fall back to prefix
  // handling for paths outside the marketing route registry.
  const svPath =
    localizedSvPath !== normalizedPath
      ? localizedSvPath
      : normalizedPath.startsWith("/en")
        ? normalizedPath.replace(/^\/en/, "") || "/"
        : normalizedPath;
  const enPath =
    localizedEnPath !== normalizedPath
      ? localizedEnPath
      : !normalizedPath.startsWith("/en")
        ? `/en${normalizedPath}`
        : normalizedPath;

  return {
    svUrl: `${BASE_URL}${svPath}`,
    enUrl: `${BASE_URL}${enPath}`,
  };
};
